"use client";

import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, X, Search, Check } from "lucide-react";
import { formatCstDateTimeLocal } from "../utils/cstDate";
import pettyCashService from "../apis/pettycash";

const PETTY_CASH_DEPARTMENTS = [
  "Bar",
  "Kitchen",
  "Poker Room",
  "4Common",
  "Brownsville",
];

const getLoginDisplayName = (currentUser) => {
  try {
    const profileRaw = localStorage.getItem("profile");
    if (profileRaw) {
      const profile = JSON.parse(profileRaw);
      if (profile?.name?.trim()) return profile.name.trim();
    }
  } catch (_) {
    /* ignore */
  }

  if (currentUser?.name?.trim()) return currentUser.name.trim();
  if (currentUser?.username?.trim()) return currentUser.username.trim();

  const storedUsername = (localStorage.getItem("username") || "").trim();
  if (storedUsername) return storedUsername;

  return "";
};

const resolveLoginFullName = (employees, currentUser) => {
  const loginName = getLoginDisplayName(currentUser);
  if (!loginName) return "";

  const all = (employees || []).filter(Boolean);
  const loginLower = loginName.toLowerCase();

  const exact = all.find((emp) => emp.toLowerCase() === loginLower);
  if (exact) return exact;

  const startsWithWord = all.find((emp) =>
    emp.toLowerCase().startsWith(`${loginLower} `)
  );
  if (startsWithWord) return startsWithWord;

  const firstToken = loginLower.split(/\s+/)[0];
  const byFirstName = all.find((emp) => {
    const empFirst = emp.toLowerCase().split(/\s+/)[0];
    return empFirst === firstToken;
  });
  if (byFirstName) return byFirstName;

  return loginName;
};

const buildEmployeeOptions = (employees, currentUser, search = "") => {
  const all = [...(employees || [])].filter(Boolean);
  const loginFullName = resolveLoginFullName(all, currentUser);

  let ordered;
  if (loginFullName) {
    const rest = all
      .filter((emp) => emp.toLowerCase() !== loginFullName.toLowerCase())
      .sort((a, b) => a.localeCompare(b));
    ordered = [loginFullName, ...rest];
  } else {
    ordered = all.sort((a, b) => a.localeCompare(b));
  }

  if (!search.trim()) return ordered;
  const searchLower = search.toLowerCase();
  return ordered.filter((emp) => emp.toLowerCase().includes(searchLower));
};

export const PettyCashEditModal = ({
  isOpen,
  onClose,
  transaction,
  onUpdateTransaction,
  currentUser,
}) => {
  const [formData, setFormData] = useState({
    date: "",
    name: "",
    department: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await pettyCashService.getEmployees();
        const employeeList = response?.data || [];
        setEmployees(employeeList);
      } catch (error) {
        console.error("Failed to fetch employees", error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredEmployees(buildEmployeeOptions(employees, currentUser, value));
  };

  useEffect(() => {
    setFilteredEmployees(buildEmployeeOptions(employees, currentUser, searchTerm));
  }, [employees, currentUser, searchTerm]);

  useEffect(() => {
    if (transaction) {
      setFormData({
        date: formatCstDateTimeLocal(transaction.date),
        name: transaction.name || "",
        department: transaction.department || "",
        notes: transaction.notes || "",
      });
      setError(null);
    }
  }, [transaction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!transaction) return;

    const name = formData.name.trim();
    const notes = formData.notes.trim();
    const date = formData.date;
    const department = formData.department;

    if (!date) {
      setError("Please select a valid date and time.");
      return;
    }

    if (!name) {
      setError("Please enter the name of the person.");
      return;
    }

    if (!department) {
      setError("Please select a department.");
      return;
    }

    if (!notes) {
      setError("Please enter transaction details.");
      return;
    }

    setSubmitting(true);
    const isDateUnchanged =
      formData.date === formatCstDateTimeLocal(transaction.date);

    try {
      const success = await onUpdateTransaction(transaction.id, {
        date: isDateUnchanged ? transaction.date : formData.date,
        name,
        department,
        notes,
      });

      if (success) {
        handleClose();
        toast.success("Transaction updated successfully!");
      }
    } catch (error) {
      console.error("Transaction update error:", error);
      setError("Failed to update transaction. Please try again.");
      toast.error("Failed to update transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      date: "",
      name: "",
      department: "",
      notes: "",
    });
    setError(null);
    setSearchTerm("");
    setDropdownOpen(null);
    onClose();
  };

  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Record</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full"
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium">Cash Given To</label>
            <div className="relative" ref={dropdownRef}>
              <div
                className="border rounded-md p-2 flex justify-between items-center cursor-pointer"
                onClick={() =>
                  setDropdownOpen(
                    dropdownOpen === "employee" ? null : "employee"
                  )
                }
              >
                {formData.name ? (
                  <div className="flex items-center gap-2">
                    <span>{formData.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData((prev) => ({ ...prev, name: "" }));
                      }}
                    >
                      <X size={16} className="text-gray-500" />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400">Select employee</span>
                )}
                <svg
                  className={`w-4 h-4 ml-2 ${
                    dropdownOpen === "employee" ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {dropdownOpen === "employee" && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="py-1">
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp) => (
                        <div
                          key={emp}
                          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, name: emp }));
                            setDropdownOpen(null);
                          }}
                        >
                          <div
                            className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                              formData.name === emp
                                ? "bg-blue-500 text-white"
                                : "border-gray-300"
                            }`}
                          >
                            {formData.name === emp && (
                              <Check className="h-4 w-4" />
                            )}
                          </div>
                          <span>{emp}</span>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-gray-500 text-center">
                        No employees found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <Select
              value={formData.department}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, department: value }))
              }
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {(
                  formData.department &&
                  !PETTY_CASH_DEPARTMENTS.includes(formData.department)
                    ? [formData.department, ...PETTY_CASH_DEPARTMENTS]
                    : PETTY_CASH_DEPARTMENTS
                ).map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Details
            </label>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || "Current User"}
              disabled
              className="w-full bg-gray-100"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
